import React from 'react';
import { useLoaderData, useParams } from '@tanstack/react-router';
import {
  SurveyModel, CompletingEvent,
} from 'survey-core';
import Survey from '../survey/survey';
import {
  isApiError, isExpiredToken, isNotFoundError, isBadRequestError,
  isUnauthorizedError,
} from '../../utils/errors';
import { useStatusMessageDispatch } from '../../hooks/StatusMessageContext';
import ErrorPage from '../status-pages/error-page';
import { TokenExpirationMessage } from '../token-expiration-message/token-expiration-message';
import { displayCustomPage } from '../../utils/custom-page/display-custom-page';
import { submitAdditionalThirdParties } from '../../api/submissions/submit-additional-third-parties';
import { getSurveyModel } from '../survey-questions/get-survey-model';
import { AdditionalThirdPartiesLoaderResponse } from '../../routes/$accountSlug/$submissionId/additional-third-parties/$type';
import { ThirdPartyType } from '../../types';
import i18n from '../../i18n';

const THIRD_PARTIES_PANEL_NAME = 'thirdParties';

const THIRD_PARTY_CONFIG = {
  [ThirdPartyType.TRADE_REFERENCE]: {
    submittedKey: 'components:pages.additional_trade_references_submitted',
    errorMessage: 'components:pages.additional_trade_references_error',
    label: 'trade references',
  },
  [ThirdPartyType.PERSONAL_GUARANTOR]: {
    submittedKey: 'components:pages.additional_personal_guarantors_submitted',
    errorMessage: 'components:pages.additional_personal_guarantor_error',
    label: 'personal guarantors',
  },
} as const;

const AdditionalThirdParties: React.FC = () => {
  const {
    settings,
    type,
    error,
  } = useLoaderData({ from: '/$accountSlug/$submissionId/additional-third-parties/$type' }) as AdditionalThirdPartiesLoaderResponse;

  const { setStatusMessage } = useStatusMessageDispatch();
  const { accountSlug, submissionId } = useParams({ from: '/$accountSlug/$submissionId/additional-third-parties/$type' });

  const config = type ? THIRD_PARTY_CONFIG[type] : THIRD_PARTY_CONFIG[ThirdPartyType.TRADE_REFERENCE];

  // This route is only used after token validation and redirect, so errors here are not token-related
  if (isNotFoundError(error) || isUnauthorizedError(error) || isBadRequestError(error)) {
    return <ErrorPage
      errorType={error}
      link={`/${accountSlug}/`}
      thirdPartyType={type}
           />;
  }

  const handleSubmission = async (sender: SurveyModel, options: CompletingEvent) => {
    try {
      // Additional validation for third parties count
      const thirdPartiesPanel = sender.getQuestionByName(THIRD_PARTIES_PANEL_NAME);
      if (thirdPartiesPanel && thirdPartiesPanel.minPanelCount !== thirdPartiesPanel.panels.length) {
        options.allow = false;
        setStatusMessage({
          severity: 'error',
          message: `You must provide exactly ${thirdPartiesPanel.minPanelCount} complete ${config.label}. Currently provided: ${thirdPartiesPanel.panels.length}`,
        });
        return;
      }

      const responses = sender.data;
      await submitAdditionalThirdParties(
        accountSlug,
        submissionId,
        responses,
        type,
      );

      sender.readOnly = true;
      options.allow = true;
    } catch (err) {
      options.allow = false;
      if (isApiError(err) && isExpiredToken(err)) {
        displayCustomPage({
          survey: sender,
          component: TokenExpirationMessage,
          getProps: () => ({
            publicId: submissionId,
            slug: accountSlug,
            thirdPartyType: type,
          }),
          withoutFrame: true,
        });

        sender.readOnly = true;
      } else {
        setStatusMessage({
          severity: 'error',
          message: isApiError(err) ? err.message : i18n.t(config.errorMessage),
        });
      }
    }
  };

  const surveyModel = getSurveyModel({
    settings,
    onCompleting: handleSubmission,
    completedHtml: i18n.t(config.submittedKey),
  });

  return (
    <Survey surveyModel={surveyModel} />
  );
};

export default AdditionalThirdParties;
